import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import './OnlineSession.css';

function OnlineSession() {
  const { courseId, duration } = useParams();
  const numericDuration = parseInt(duration, 10);
  const [tutors, setTutors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pendingRequest, setPendingRequest] = useState(null);
  const [declinedRequest, setDeclinedRequest] = useState(null);

  useEffect(() => {
    const fetchTutors = async () => {
      try {
        const auth = getAuth();
        const token = await auth.currentUser.getIdToken();

        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/tutors/online/${courseId}`,
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );

        setTutors(response.data);
      } catch (err) {
        setError('Failed to fetch tutors.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchTutors();
  }, [courseId]);

  useEffect(() => {
    if (pendingRequest) {
      const timer = setTimeout(() => {
        setPendingRequest(null);
      }, 30000); // 30 seconds

      return () => clearTimeout(timer);
    }
  }, [pendingRequest]);

  useEffect(() => {
    if (!pendingRequest) return;

    const checkRequestStatus = async () => {
      const auth = getAuth();
      const token = await auth.currentUser.getIdToken();

      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/session-requests/${pendingRequest.requestId}`,
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );

        const { status, sessionId, startsAt } = response.data;

        if (status === 'rejected') {
          setPendingRequest(null);
          setDeclinedRequest(pendingRequest.requestId);
          setTimeout(() => setDeclinedRequest(null), 3000);
        }

        if (status === 'accepted' && sessionId && startsAt) {
          window.location.href = `/dashboard/waiting/${sessionId}`;
          const timeLeft = Math.max(startsAt - Date.now(), 0);
          setTimeout(() => {
            window.location.href = `/dashboard/video/${sessionId}`;
          }, timeLeft);
        }
      } catch (error) {
        console.error('Error checking request status:', error);
      }
    };

    const intervalId = setInterval(checkRequestStatus, 2000);
    return () => clearInterval(intervalId);
  }, [pendingRequest]);

  const handleRequestSession = async (tutor) => {
    const auth = getAuth();
    const token = await auth.currentUser.getIdToken();

    try {
      // Fetch user's points
      const userRes = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/user/${auth.currentUser.uid}/data`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      const userPoints = userRes.data.points ?? 0;

      // Correct session price calculation
      const sessionPrice = (numericDuration / 60) * tutor.onlinePrice;

      if (userPoints < sessionPrice) {
        alert(
          `You don't have enough points. This session costs R${sessionPrice}, but you only have R${userPoints}.`
        );
        return;
      }

      const res = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/session-requests`,
        {
          tutorId: tutor.uid,
          courseId,
          duration: numericDuration,
          type: 'Online',
          price: sessionPrice
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setPendingRequest({
        tutorId: tutor.uid,
        requestId: res.data.requestId,
        expiresAt: Date.now() + 30000
      });

      // 2️⃣ Send push notification to tutor
    await axios.post(
      `${process.env.REACT_APP_API_BASE_URL}/notify-tutor`,
      {
        tutorId: tutor.uid,
        sessionId: res.data.requestId,
        courseName: tutor.courseName || 'Course',
        studentName: auth.currentUser.displayName || 'Student',
        type: 'Online'
      },
      {
        headers: { Authorization: `Bearer ${token}` }
      }
    );
    } catch (err) {
      console.error('Error requesting session:', err);
      if (err.response?.data?.error) {
        alert(err.response.data.error);
      } else {
        alert('Failed to request session');
      }
    }
  };

  if (loading) return <div>Loading tutors...</div>;
  if (error) return <div style={{ color: 'red' }}>{error}</div>;

  return (
    <div className="online-session-container">
      <h2>Online Session Tutors</h2>

      {pendingRequest && (
        <div className="pending-request-banner">
          <p>
            Waiting for response from tutor... You can request another tutor in{' '}
            {Math.ceil((pendingRequest.expiresAt - Date.now()) / 1000)} seconds
          </p>
        </div>
      )}

      {declinedRequest && (
        <div className="declined-request-banner">
          <p>Your session request was declined by the tutor.</p>
        </div>
      )}

      {tutors.length === 0 ? (
        <p>No tutors available for this course.</p>
      ) : (
        <div>
          <h3>Currently available tutors:</h3>
          <ul className="tutor-list">
            {tutors.map((tutor, index) => {
              const calculatedPrice = ((numericDuration / 60) * tutor.onlinePrice).toFixed(2);
              return (
                <li key={index} className="tutor-item">
                  <div className="tutor-info-container">
                    <div className="tutor-details">
                      <p><strong>{tutor.username}</strong></p>
                      <p>Price for this session: R{calculatedPrice}</p>
                    </div>
                    <div className="tutor-profile-pic-container">
                      <img
                        src={tutor.profilePictureUrl || '/default-profile.png'}
                        alt={`${tutor.username} profile`}
                        className="tutor-profile-pic"
                      />
                    </div>
                  </div>
                  <button
                    onClick={() => handleRequestSession(tutor)}
                    disabled={pendingRequest !== null && pendingRequest.tutorId === tutor.uid}
                  >
                    {pendingRequest && pendingRequest.tutorId === tutor.uid
                      ? 'Request Pending...'
                      : 'Request Session'}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

export default OnlineSession;
